import { NmLoader } from './NmLoader';
import { displayDigits } from '../utils/displayDigits';
import React, { useEffect, useId, useState } from 'react';

export type NmCircularScoreVariant = 'success' | 'warning' | 'pink' | 'neutral' | 'danger' | 'info';

interface NmCircularScoreProps {
  /** Score 0–100. `null` / `undefined` shows the shared brand loader. */
  value: number | null | undefined;
  /** Optional metric value rendered in the ring while `value` controls its fill. */
  displayValue?: number;
  /** Unit rendered beside the value. Defaults to `%`; pass an empty string for unitless metrics. */
  unit?: string;
  variant?: NmCircularScoreVariant;
  /** Direction the progress arc grows from 12 o'clock. */
  direction?: 'clockwise' | 'counterclockwise';
  size?: number;
  /** Show the shared brand loader (e.g. while fetching). */
  loading?: boolean;
  className?: string;
  'aria-label'?: string;
}

const R = 18;
const C = 2 * Math.PI * R;

/**
 * Compact neumorphic circular score ring with mount animation
 * (stroke fill + count-up). Used in coach athlete headers for readiness %.
 */
export function NmCircularScore({
  value,
  displayValue,
  unit = '%',
  variant = 'neutral',
  direction = 'clockwise',
  size = 44,
  loading = false,
  className = '',
  'aria-label': ariaLabel,
}: NmCircularScoreProps) {
  const gradId = useId().replace(/:/g, '');
  const indeterminate = loading || value == null || Number.isNaN(value);
  const target = indeterminate ? 0 : Math.max(0, Math.min(100, Math.round(value)));
  const displayTarget = indeterminate ? 0 : Math.round(displayValue ?? value);

  const [display, setDisplay] = useState(0);
  const [progress, setProgress] = useState(0);
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    if (indeterminate) {
      setDisplay(0);
      setProgress(0);
      setEntered(false);
      return;
    }

    setEntered(false);
    setProgress(0);
    setDisplay(0);

    const preferReduced =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (preferReduced) {
      setProgress(target);
      setDisplay(displayTarget);
      setEntered(true);
      return;
    }

    // Kick CSS enter on next frame, then ease stroke + count-up together.
    const raf = requestAnimationFrame(() => {
      setEntered(true);
      setProgress(target);
    });

    const durationMs = 900;
    const start = performance.now();
    let frame = 0;

    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / durationMs);
      // easeOutCubic
      const eased = 1 - (1 - t) ** 3;
      setDisplay(Math.round(displayTarget * eased));
      if (t < 1) {
        frame = requestAnimationFrame(tick);
      } else {
        setDisplay(displayTarget);
      }
    };
    frame = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      cancelAnimationFrame(frame);
    };
  }, [displayTarget, indeterminate, target]);

  if (indeterminate) return <NmLoader size={size} inline className={className} />;

  const offset = C - (progress / 100) * C;
  const label = ariaLabel ?? (indeterminate ? undefined : `${displayDigits(displayTarget)}${unit}`);

  return (
    <span
      className={`nm-circular-score nm-circular-score--${variant}${direction === 'counterclockwise' ? ' nm-circular-score--counterclockwise' : ''}${indeterminate ? ' nm-circular-score--loading' : ''}${entered ? ' is-entered' : ''} ${className}`.trim()}
      style={{ width: size, height: size }}
      role="img"
      aria-label={label}
      aria-busy={indeterminate || undefined}
    >
      <svg viewBox="0 0 44 44" width={size} height={size} aria-hidden className="nm-circular-score__svg">
        <defs>
          <linearGradient id={`nm-cs-grad-${gradId}`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" className="nm-circular-score__stop-a" />
            <stop offset="100%" className="nm-circular-score__stop-b" />
          </linearGradient>
        </defs>
        <circle className="nm-circular-score__track" cx="22" cy="22" r={R} />
        <circle
          className="nm-circular-score__fill"
          cx="22"
          cy="22"
          r={R}
          stroke={`url(#nm-cs-grad-${gradId})`}
          strokeDasharray={indeterminate ? `${C * 0.28} ${C}` : C}
          strokeDashoffset={indeterminate ? 0 : offset}
        />
      </svg>
      <span className="nm-circular-score__value" aria-hidden>
        {indeterminate ? (
          '···'
        ) : (
          <>
            {displayDigits(display)}
            {unit && <small className="nm-circular-score__unit">{unit}</small>}
          </>
        )}
      </span>
    </span>
  );
}
